import React, { useCallback, useMemo, useState } from 'react';

import { Stack, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconEdit,
  IconRotateClockwise2,
  IconTrash,
  IconArrowBackUp,
  IconHandStop,
  IconPencil,
} from '@tabler/icons-react';
import CreditsProgressBar from 'components/CreditsProgressBar';
import Menu, { MenuItemProps } from 'components/Menu';
import { MRT_ColumnDef, MRT_Row, MantineReactTable } from 'mantine-react-table';
import { AthletePackageRetrieve, AttendanceRuleRetrieve, CreditDispensation } from 'types';
import { getPooledCreditsByDispenseDate } from 'utils/athlete-package.helpers';
import {
  dateRangeContainsPresentDate,
  getDateTimePeriod,
  isDateFuture,
  isDatePast,
} from 'utils/date-functions';

import ModalCreditDispensationDelete from './ModalCreditDispensationDelete';
import ModalCreditDispensationEdit from './ModalCreditDispensationEdit';
import ModalCreditDispensationUndo from './ModalCreditDispensationUndo';
import ValidityDatesCell from './ValidityDatesCell/ValidityDatesCell';

type TimeFrame = 'past' | 'present' | 'future';

type CreditDispensationUiState = TimeFrame;

type CreditDispensationWithUiState = CreditDispensation & {
  uiState: CreditDispensationUiState;
};

interface ICreditDispensationTableProps {
  athletePackage: AthletePackageRetrieve;
  attendanceRules: AttendanceRuleRetrieve[];
  permission?: boolean;
}

const compareAsc = (a: string | null | undefined, b: string | null | undefined): number => {
  // null = "no expiry", treat as far in the future
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

const getUiStateForCreditDispensation = (
  creditDispensation: CreditDispensation,
): CreditDispensationUiState => {
  return getDateTimePeriod(
    creditDispensation.dispenseDate,
    creditDispensation.expirationDate,
  ) as CreditDispensationUiState;
};

const sortCreditDispensationsWithUiState = (
  creditDispensations: CreditDispensation[],
): CreditDispensationWithUiState[] => {
  const withUiState: CreditDispensationWithUiState[] = creditDispensations.map(cd => ({
    ...cd,
    uiState: getUiStateForCreditDispensation(cd),
  }));

  const sorted: CreditDispensationWithUiState[] = [];
  const rollovers: CreditDispensationWithUiState[] = [];
  withUiState.forEach(cd => {
    if (!cd.parentCreditDispensation) {
      sorted.push(cd);
    } else {
      rollovers.push(cd);
    }
  });
  rollovers.forEach(cd => {
    const parentIndex = sorted.findIndex(
      parentCd => parentCd.creditDispensationId === cd.parentCreditDispensation,
    );
    if (parentIndex !== -1) {
      sorted.splice(parentIndex + 1, 0, cd);
    } else {
      sorted.push(cd);
    }
  });

  return sorted;
};

const getCreditDispensationsGroupedByAttendanceRule = (
  creditDispensations: CreditDispensation[],
): Record<string, CreditDispensation[]> => {
  const map: Record<string, CreditDispensation[]> = {};
  for (let i = 0; i < creditDispensations.length; i += 1) {
    const cd = creditDispensations[i];
    // `attendanceRule` is the rule's id since ENG-2308 stopped nesting the whole rule
    // here. Grouping is unchanged: still keyed on the rule, and dispensations sharing a
    // rule grant the same event types.
    if (!cd.attendanceRule) continue;
    if (map[cd.attendanceRule]) {
      map[cd.attendanceRule].push(cd);
    } else {
      map[cd.attendanceRule] = [cd];
    }
  }
  return map;
};

const getCreditDispensationsGroupedByTimeFrame = (
  creditDispensations: CreditDispensation[],
): Record<TimeFrame, CreditDispensation[]> => {
  const past = creditDispensations
    // only consider non-null expirations as "past"
    .filter(cd => cd.expirationDate !== null && isDatePast(cd.expirationDate))
    .sort((a, b) => compareAsc(a.expirationDate, b.expirationDate));

  const present = creditDispensations
    .filter(cd => dateRangeContainsPresentDate(cd.dispenseDate, cd.expirationDate))
    .sort((a, b) => compareAsc(a.expirationDate, b.expirationDate));

  const future = creditDispensations
    .filter(cd => isDateFuture(cd.dispenseDate))
    .sort((a, b) => compareAsc(a.dispenseDate, b.dispenseDate));

  return { past, present, future };
};

const getRelevantCreditDispensation = (
  creditDispensations: Record<TimeFrame, CreditDispensation[]>,
): CreditDispensation | undefined => {
  const { past, present, future } = creditDispensations;

  // 1. Present, non-rollover
  const presentNonRollover = present.find(cd => !cd.isRollover);
  if (presentNonRollover) return presentNonRollover;

  // 2. Present, rollover
  const presentRollover = present.find(cd => cd.isRollover);
  if (presentRollover) return presentRollover;

  // 3. Last past credit
  if (past.length > 0) return past[past.length - 1];

  // 4. First future credit
  if (future.length > 0) return future[0];

  return undefined;
};

const CreditDispensationTable = ({
  athletePackage,
  attendanceRules,
  permission = true,
}: ICreditDispensationTableProps): JSX.Element => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const sortedCreditDispensations = athletePackage.creditDispensations.sort((a, b) =>
    a.dispenseDate < b.dispenseDate ? -1 : 1,
  );
  const attendanceRuleToCreditDispensationsDict =
    getCreditDispensationsGroupedByAttendanceRule(sortedCreditDispensations);

  const [isEditModalOpen, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isUndoModalOpen, { open: openUndoModal, close: closeUndoModal }] = useDisclosure(false);
  const [selectedCD, setSelectedCD] = useState<CreditDispensation | null>(null);

  const packageType = athletePackage.package.type;
  const pooledCreditsByDispenseDate = getPooledCreditsByDispenseDate(sortedCreditDispensations);

  const handleExpandButtonClick = (row: MRT_Row<any>): void => {
    const creditDispensations = attendanceRuleToCreditDispensationsDict[row.original.attendanceRuleId];
    if (creditDispensations.length > 1) {
      row.toggleSelected();
    }
  };

  const handleEditClick = (creditDispensation: CreditDispensation): void => {
    setSelectedCD(creditDispensation);
    openEditModal();
  };

  const handleUndoClick = (creditDispensation: CreditDispensation): void => {
    setSelectedCD(creditDispensation);
    openUndoModal();
  };

  const handleDeleteClick = (creditDispensation: CreditDispensation): void => {
    setSelectedCD(creditDispensation);
    openDeleteModal();
  };

  const ManualEditIcon = (): JSX.Element => (
    <div style={{ position: 'relative', width: 18, height: 18 }}>
      <IconHandStop size={18} style={{ position: 'absolute', color: 'gray', left: 0, top: 0 }} />
      <IconPencil size={12} style={{ position: 'absolute', color: 'gray', right: 1, bottom: -2 }} />
    </div>
  );

  const getMenuItems = (creditDispensation: CreditDispensation): MenuItemProps[] => {
    const isRollover = creditDispensation.isRollover;
    const hasUsedCredits = creditDispensation.creditsUsed > 0;

    const items: MenuItemProps[] = [
      {
        title: 'Edit',
        onClick: () => handleEditClick(creditDispensation),
        disabled: creditDispensation.hasRolledOver,
        icon: <IconEdit size={theme.fontSizes.sm} />,
      },
      {
        title: 'Delete',
        color: 'red',
        onClick: () => handleDeleteClick(creditDispensation),
        disabled: creditDispensation.hasRolledOver,
        icon: <IconTrash size={theme.fontSizes.sm} />,
      },
    ];

    if (isRollover) {
      items.push({
        title: 'Undo',
        onClick: () => handleUndoClick(creditDispensation),
        // Only allow undo for rollover CDs with no used credits
        disabled: hasUsedCredits,
        icon: <IconArrowBackUp size={theme.fontSizes.sm} />,
      });
    }

    return items;
  };

  const renderValidityPeriod = useCallback(
    (row: MRT_Row<any>): JSX.Element => {
      const creditDispensations =
        attendanceRuleToCreditDispensationsDict[row.original.attendanceRuleId] || [];
      const creditDispensationsByTimeFrame = getCreditDispensationsGroupedByTimeFrame(creditDispensations);
      const relevantCreditDispensation = getRelevantCreditDispensation(creditDispensationsByTimeFrame);
      if (row.getIsExpanded()) {
        const sortedWithUiState = sortCreditDispensationsWithUiState(creditDispensations);

        return (
          <Stack>
            {sortedWithUiState.map(cd => (
              <div
                key={cd.creditDispensationId}
                style={{ height: '24.5px', display: 'flex', alignItems: 'center' }}
              >
                <ValidityDatesCell
                  packageType={packageType}
                  creditDispensation={cd}
                  pooledCredits={pooledCreditsByDispenseDate[cd.dispenseDate]}
                  timeFrame={cd.uiState}
                />
              </div>
            ))}
          </Stack>
        );
      }
      if (!row.getIsExpanded() && relevantCreditDispensation) {
        return (
          <ValidityDatesCell
            packageType={packageType}
            creditDispensation={relevantCreditDispensation}
            pooledCredits={pooledCreditsByDispenseDate[relevantCreditDispensation.dispenseDate]}
            timeFrame={getUiStateForCreditDispensation(relevantCreditDispensation)}
            key={relevantCreditDispensation.creditDispensationId}
          />
        );
      }
      return <></>;
    },
    [attendanceRuleToCreditDispensationsDict, packageType, pooledCreditsByDispenseDate],
  );

  const renderCreditUsage = useCallback(
    (row: MRT_Row<any>): JSX.Element => {
      const creditDispensations =
        attendanceRuleToCreditDispensationsDict[row.original.attendanceRuleId] || [];
      const creditDispensationsByTimeFrame = getCreditDispensationsGroupedByTimeFrame(creditDispensations);
      const relevantCreditDispensation = getRelevantCreditDispensation(creditDispensationsByTimeFrame);
      if (row.getIsExpanded()) {
        const sortedWithUiState = sortCreditDispensationsWithUiState(creditDispensations);

        return (
          <Stack>
            {sortedWithUiState.map(cd => (
              <CreditsProgressBar
                key={cd.creditDispensationId}
                totalCredits={cd.credits}
                creditsUsed={cd.creditsUsed}
                height='24.5px'
              />
            ))}
          </Stack>
        );
      }

      if (!row.getIsExpanded() && relevantCreditDispensation) {
        return (
          <CreditsProgressBar
            totalCredits={relevantCreditDispensation.credits}
            creditsUsed={relevantCreditDispensation.creditsUsed}
          />
        );
      }
      return <></>;
    },
    [attendanceRuleToCreditDispensationsDict],
  );

  const renderIsRollover = useCallback(
    (row: MRT_Row<any>): JSX.Element => {
      const creditDispensations =
        attendanceRuleToCreditDispensationsDict[row.original.attendanceRuleId] || [];
      const creditDispensationsByTimeFrame = getCreditDispensationsGroupedByTimeFrame(creditDispensations);
      const relevantCreditDispensation = getRelevantCreditDispensation(creditDispensationsByTimeFrame);

      // helper to render icons for a single credit dispensation
      const renderIconsForCd = (cd: CreditDispensation): JSX.Element => (
        <div
          key={cd.creditDispensationId}
          style={{ height: '24.5px', display: 'flex', alignItems: 'center', gap: 4, marginLeft: '12px' }}
        >
          {cd.isRollover && <IconRotateClockwise2 style={{ color: 'black' }} />}
          {cd.manualEdit && <ManualEditIcon />}
        </div>
      );

      if (row.getIsExpanded()) {
        const sortedWithUiState = sortCreditDispensationsWithUiState(creditDispensations);

        return <Stack>{sortedWithUiState.map(cd => renderIconsForCd(cd))}</Stack>;
      }

      if (!row.getIsExpanded() && relevantCreditDispensation) {
        return renderIconsForCd(relevantCreditDispensation);
      }

      return <></>;
    },
    [attendanceRuleToCreditDispensationsDict],
  );

  const renderActionsMenu = useCallback(
    (row: MRT_Row<any>): JSX.Element => {
      const creditDispensations =
        attendanceRuleToCreditDispensationsDict[row.original.attendanceRuleId] || [];
      const creditDispensationsByTimeFrame = getCreditDispensationsGroupedByTimeFrame(creditDispensations);
      const relevantCreditDispensation = getRelevantCreditDispensation(creditDispensationsByTimeFrame);
      if (row.getIsExpanded()) {
        const sortedWithUiState = sortCreditDispensationsWithUiState(creditDispensations);

        return (
          <Stack>
            {sortedWithUiState.map(cd => (
              <Menu
                disabled={!permission}
                key={cd.creditDispensationId}
                items={getMenuItems(cd)}
                withinPortal={true}
                withArrow
              />
            ))}
          </Stack>
        );
      }

      if (!row.getIsExpanded() && relevantCreditDispensation) {
        return (
          <Menu
            disabled={!permission}
            items={getMenuItems(relevantCreditDispensation)}
            withArrow
            withinPortal={true}
          />
        );
      }
      return <></>;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attendanceRuleToCreditDispensationsDict],
  );

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        Header: (
          <Text fs='italic' c='dimmed' pr='xs'>
            Event Type
          </Text>
        ),
        header: 'name',
        accessorKey: 'name',
        size: isDesktop ? 180 : 100,
        Cell: ({ row }: { row: MRT_Row<any> }) => {
          return <Text fw='bold'>{row.original.name}</Text>;
        },
      },
      {
        Header: (
          <Text fs='italic' c='dimmed' pr='xs'>
            Validity
          </Text>
        ),
        header: 'attendanceRuleId',
        size: isDesktop ? 180 : 100,
        Cell: ({ row }: { row: MRT_Row<any> }) => renderValidityPeriod(row),
      },
      {
        Header: (
          <Text fs='italic' c='dimmed' pr='xs'>
            Credit Usage
          </Text>
        ),
        header: 'credits',
        size: isDesktop ? 180 : 100,
        Cell: ({ row }: { row: MRT_Row<any> }) => renderCreditUsage(row),
      },
      {
        Header: (
          <Text fs='italic' c='dimmed' pr='xs'>
            Notes
          </Text>
        ),
        size: 80,
        header: 'isRollover',
        Cell: ({ row }: { row: MRT_Row<any> }) => renderIsRollover(row),
      },
      {
        Header: '',
        header: 'action',
        size: 50,
        Cell: ({ row }: { row: MRT_Row<any> }) => renderActionsMenu(row),
      },
    ],
    [renderCreditUsage, renderValidityPeriod, renderIsRollover, renderActionsMenu, isDesktop],
  );

  return (
    <>
      <MantineReactTable
        columns={columns}
        data={attendanceRules}
        enableBottomToolbar={false}
        enableColumnActions={false}
        enableDensityToggle={false}
        enableExpanding
        enableExpandAll={false}
        enableFilters={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableMultiRowSelection={false}
        enablePagination={false}
        enableRowSelection={false}
        enableSorting={false}
        enableTableFooter={false}
        enableTopToolbar={false}
        manualFiltering
        manualSorting
        getRowCanExpand={() => {
          return true;
        }}
        displayColumnDefOptions={{ 'mrt-row-expand': { size: 10, Header: '' } }}
        mantinePaperProps={{
          shadow: 'sm',
        }}
        mantineExpandButtonProps={({ row }: any) => ({
          onClick: () => handleExpandButtonClick(row),
          sx: {
            display:
              (attendanceRuleToCreditDispensationsDict[row.original.attendanceRuleId] || []).length > 1
                ? 'default'
                : 'none',
          },
        })}
      />
      <ModalCreditDispensationEdit
        opened={isEditModalOpen}
        handleClose={closeEditModal}
        creditDispensation={selectedCD}
      />
      <ModalCreditDispensationDelete
        opened={isDeleteModalOpen}
        handleClose={closeDeleteModal}
        creditDispensation={selectedCD}
      />
      <ModalCreditDispensationUndo
        opened={isUndoModalOpen}
        handleClose={closeUndoModal}
        creditDispensation={selectedCD}
      />
    </>
  );
};

export default CreditDispensationTable;
